import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send, Webhook, CheckCircle2, XCircle, Copy } from "lucide-react";

const ALL_EVENTS = [
  { key: "email.priority.new", label: "Nouveau email prioritaire" },
  { key: "summary.generated", label: "Résumé IA généré" },
];

export default function Webhooks() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(ALL_EVENTS.map(e => e.key));
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/webhooks");
      setItems(r.data);
    } catch (err) { toast.error("Erreur"); }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (events.length === 0) {
      toast.error("Sélectionnez au moins un événement");
      return;
    }
    setLoading(true);
    try {
      await api.post("/webhooks", { url, events, is_active: true });
      toast.success("Webhook créé");
      setUrl("");
      setEvents(ALL_EVENTS.map(e => e.key));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce webhook ?")) return;
    try {
      await api.delete(`/webhooks/${id}`);
      toast.success("Supprimé");
      await load();
    } catch (err) { toast.error("Erreur"); }
  };

  const test = async (id) => {
    try {
      const r = await api.post(`/webhooks/${id}/test`);
      if (r.data.ok) toast.success(`OK (HTTP ${r.data.status})`);
      else toast.error(`Échec : HTTP ${r.data.status || "?"}${r.data.error ? " — " + r.data.error : ""}`);
      await load();
    } catch (err) { toast.error("Erreur"); }
  };

  const copySecret = (secret) => {
    navigator.clipboard.writeText(secret);
    toast.success("Secret copié");
  };

  const toggleEvent = (key) => {
    setEvents(prev => prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]);
  };

  return (
    <div className="p-6 md:p-8" data-testid="webhooks-page">
      <div className="mb-8">
        <div className="overline mb-1">// Intégrations</div>
        <h1 className="text-3xl font-bold tracking-tight mb-2" style={{fontFamily:"'Work Sans', sans-serif"}}>
          Webhooks
        </h1>
        <p className="text-gray-600 text-sm flex items-center gap-2">
          <Webhook className="w-4 h-4 text-[#002FA7]" />
          Notifiez vos systèmes (Zapier, n8n, Slack…) lors d'événements. Chaque appel est signé HMAC-SHA256 dans l'en-tête <code className="bg-gray-100 px-1 py-0.5 text-xs">X-SmartMail-Signature</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 p-6">
          <div className="overline mb-1">// Nouveau</div>
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily:"'Work Sans', sans-serif"}}>Ajouter un webhook</h3>
          <form onSubmit={add} className="space-y-4" data-testid="webhook-form">
            <div>
              <label className="overline block mb-2">URL cible</label>
              <input type="url" value={url} onChange={(e)=>setUrl(e.target.value)}
                data-testid="webhook-url-input"
                placeholder="https://hooks.zapier.com/…"
                className="w-full h-11 px-3 border border-gray-300 focus:border-[#002FA7] outline-none text-sm" required />
            </div>
            <div>
              <label className="overline block mb-2">Événements</label>
              <div className="space-y-2">
                {ALL_EVENTS.map(ev => (
                  <label key={ev.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={events.includes(ev.key)}
                      onChange={()=>toggleEvent(ev.key)}
                      data-testid={`webhook-event-${ev.key}`}
                      className="accent-[#002FA7]" />
                    <span className="text-gray-800">{ev.label}</span>
                    <code className="text-xs text-gray-500 ml-auto bg-gray-100 px-1.5 py-0.5">{ev.key}</code>
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="webhook-add-btn"
              className="w-full h-11 bg-[#002FA7] hover:bg-[#002277] text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Créer
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="overline mb-1">// Actifs ({items.length})</div>
            <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>Webhooks configurés</h3>
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">Aucun webhook. Ajoutez-en un à gauche.</div>
          ) : (
            <ul className="divide-y divide-gray-200" data-testid="webhooks-list">
              {items.map((w) => (
                <li key={w.id} className="p-5" data-testid={`webhook-row-${w.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-gray-900 break-all">{w.url}</div>
                      <div className="flex gap-1.5 flex-wrap mt-2">
                        {w.events.map(ev => (
                          <span key={ev} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-blue-50 text-[#002FA7] border border-blue-200 uppercase tracking-wider">
                            {ev}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={()=>test(w.id)} data-testid={`webhook-test-${w.id}`}
                        title="Tester"
                        className="p-2 text-gray-700 border border-gray-300 hover:border-gray-900 transition-colors">
                        <Send className="w-4 h-4" />
                      </button>
                      <button onClick={()=>remove(w.id)} data-testid={`webhook-delete-${w.id}`}
                        title="Supprimer"
                        className="p-2 text-red-700 border border-red-200 hover:border-red-700 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs pt-3 border-t border-gray-100">
                    <div>
                      <span className="overline">Secret HMAC</span>
                      <div className="flex items-center gap-1 mt-1">
                        <code className="font-mono text-gray-700 bg-gray-50 px-2 py-1 truncate flex-1">{w.secret.slice(0, 12)}…</code>
                        <button onClick={()=>copySecret(w.secret)} className="p-1 hover:bg-gray-100" title="Copier">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="overline">Dernier appel</span>
                      <div className="flex items-center gap-1 mt-1 text-gray-700">
                        {w.last_status ? (
                          <>
                            {w.last_status < 400
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                              : <XCircle className="w-3.5 h-3.5 text-red-600" />}
                            <span className="font-mono">HTTP {w.last_status}</span>
                            <span className="text-gray-500">· {w.last_delivery_at ? new Date(w.last_delivery_at).toLocaleString("fr-FR") : "—"}</span>
                          </>
                        ) : <span className="text-gray-500">Aucun envoi</span>}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
